import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setUser, setGoogleUser, logout } from '../Commonlibs/userSlice';
import { Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, TextField, Typography, Divider, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Visibility, VisibilityOff, Phone, Email, Close, Person } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Loader from '../Loader/Loader';
import houseLogo from '../assets/lob.png';
import proptitle from '../assets/titleprop.png';
import googleIcon from '../assets/google.png';
import lockIcon from '../assets/lock.png';
import { persistor } from '../Commonlibs/store';
import tited from '../assets/tited.png';

const Login = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  // NEW: State to toggle between Google and Phone login views
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  
  // Phone number related states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [dialog, setDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  
  // New state for OTP popup
  const [otpPopup, setOtpPopup] = useState({ open: false, otp: '' });
  
  // ✅ NEW: Profile verification popup states
  const [showProfileVerification, setShowProfileVerification] = useState(false);
  const [profileVerificationData, setProfileVerificationData] = useState({
    fullName: '',
    email: '',
    contactNumber: '',
    userId: null,
    token: null
  });
  const [verificationErrors, setVerificationErrors] = useState({});
  const [verificationLoading, setVerificationLoading] = useState(false);

  // FORCE GOOGLE LOGIN VIEW ON COMPONENT MOUNT
  useEffect(() => {
    setShowPhoneLogin(false);
    setShowOtpInput(false);
    setOtp('');
    setPhoneNumber('');
  }, []);

  // OPTIONAL: Clear any residual authentication data when login page loads
  useEffect(() => {
    const clearResidualAuth = () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_id');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_name');
      }
    };
    clearResidualAuth();
  }, []);

  // ✅ Validation function for profile verification fields
  const validateVerificationField = (name, value) => {
    if (!value || value.trim() === '' || value === 'N/A') {
      return `${name} is required`;
    }
    
    switch (name) {
      case 'fullName':
        return /^[A-Za-z\s]{2,50}$/.test(value)
          ? ''
          : 'Name should contain only letters and spaces (2-50 characters)';
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ''
          : 'Please enter a valid email address';
      case 'contactNumber':
        return /^\d{10}$/.test(value)
          ? ''
          : 'Phone number should be 10 digits';
      default:
        return '';
    }
  };

  // ✅ Handle verification form changes
  const handleVerificationChange = (field, value) => {
    setProfileVerificationData(prev => ({ ...prev, [field]: value }));
    if (verificationErrors[field]) {
      setVerificationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // ✅ Handle phone number input (only digits)
  const handleVerificationPhoneChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && value.length <= 10)) {
      handleVerificationChange('contactNumber', value);
    }
  };

  // ✅ Check if profile needs verification
  const checkProfileCompleteness = (userProfile) => {
    const needsVerification =
      !userProfile.full_name ||
      userProfile.full_name === 'N/A' ||
      userProfile.full_name.trim() === '' ||
      !userProfile.email_id ||
      userProfile.email_id === 'N/A' ||
      userProfile.email_id.trim() === '' ||
      !userProfile.contact_number ||
      userProfile.contact_number === 'N/A' ||
      userProfile.contact_number.trim() === '';

    return needsVerification;
  };

  // ✅ Update user profile after verification
  const updateUserProfileAfterVerification = async () => {
    setVerificationLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('user_id', profileVerificationData.userId);
      formDataToSend.append('full_name', profileVerificationData.fullName);
      formDataToSend.append('email_id', profileVerificationData.email);
      formDataToSend.append('contact_number', profileVerificationData.contactNumber);

      const response = await fetch('https://propertydealstalk.com/api/users/UpdateUserprofile', {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (data.status === "SUCCESS") {
        // Close verification popup
        setShowProfileVerification(false);
        
        // Update user data in Redux
        const updatedUser = {
          id: data.result.id,
          fullName: data.result.full_name,
          email: data.result.email_id,
          contactNumber: data.result.contact_number,
          token: profileVerificationData.token,
          authProvider: data.result.auth_provider,
          googleId: data.result.google_id,
          profileImage: data.result.profile_image,
        };

        // Update localStorage
        localStorage.setItem('user_name', data.result.full_name);
        localStorage.setItem('user_email', data.result.email_id);

        // Dispatch to Redux
        if (data.result.auth_provider === 'google') {
          dispatch(setGoogleUser(updatedUser));
        } else {
          dispatch(setUser(updatedUser));
        }

        // Navigate to sell-properties
        setLoading(true);
        setTimeout(() => {
          navigate('/sell-properties');
          setLoading(false);
        }, 1500);
      } else {
        showDialog('Error', data.message || 'Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showDialog('Error', 'Failed to update profile. Please try again.', 'error');
    } finally {
      setVerificationLoading(false);
    }
  };

  // ✅ Handle verification submit
  const handleVerificationSubmit = async () => {
    // Validate all fields
    const newErrors = {};
    Object.keys(profileVerificationData).forEach(key => {
      if (['fullName', 'email', 'contactNumber'].includes(key)) {
        const error = validateVerificationField(key, profileVerificationData[key]);
        if (error) newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setVerificationErrors(newErrors);
      return;
    }

    // Update profile
    await updateUserProfileAfterVerification();
  };

  // UPDATED: Enhanced Google OAuth redirect response handler
  useEffect(() => {
    const processGoogleResponse = async () => {
      try {
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const searchParams = new URLSearchParams(location.search);

        console.log('Checking for OAuth response...');
        console.log('Hash params:', location.hash);
        console.log('Search params:', location.search);

        const responseData = hashParams.get('response') || searchParams.get('response');
        const token = hashParams.get('token') || searchParams.get('token');
        const userId = hashParams.get('id') || searchParams.get('id') || searchParams.get('user_id');

        if (responseData) {
          console.log('Found response data in URL');
          const parsedResponse = JSON.parse(decodeURIComponent(responseData));
          console.log('Parsed Google response:', parsedResponse);

          if (parsedResponse.statusCode === 200 && parsedResponse.status === "SUCCESS") {
            const userData = parsedResponse.result;

            if (userData.token) {
              localStorage.setItem('auth_token', userData.token);
              console.log('Token stored:', userData.token);
            }

            if (userData.id) {
              await fetchAndSetUserProfile(userData.id, userData.token || localStorage.getItem('auth_token'));
            } else {
              console.log('No user ID found, using userData directly');
              handleDirectUserData(userData);
            }
          } else {
            console.error('OAuth response not successful:', parsedResponse);
            showDialog('Error', parsedResponse.message || 'Google login failed', 'error');
          }
        } else if (token && userId) {
          console.log('Found token and userId in URL params');
          localStorage.setItem('auth_token', token);
          await fetchAndSetUserProfile(userId, token);
        }

        if (responseData || token) {
          console.log('Cleaning URL...');
          window.history.replaceState({}, document.title, '/login');
        }
      } catch (error) {
        console.error('Error processing Google OAuth response:', error);
        showDialog('Error', 'Failed to process Google login. Please try again.', 'error');
        setLoading(false);
      }
    };

    const handleDirectUserData = (userData) => {
      try {
        const userToStore = {
          id: userData.id,
          fullName: userData.full_name || userData.fullName,
          email: userData.email_id || userData.email,
          contactNumber: userData.contact_number || userData.contactNumber,
          token: userData.token || localStorage.getItem('auth_token'),
          authProvider: userData.auth_provider || 'google',
          googleId: userData.google_id,
          profileImage: userData.profile_image,
        };

        localStorage.setItem('auth_token', userToStore.token);
        localStorage.setItem('user_id', userToStore.id.toString());
        localStorage.setItem('user_email', userToStore.email);
        localStorage.setItem('user_name', userToStore.fullName);

        // ✅ Check if profile needs verification
        if (checkProfileCompleteness(userData)) {
          setProfileVerificationData({
            fullName: userData.full_name && userData.full_name !== 'N/A' ? userData.full_name : '',
            email: userData.email_id && userData.email_id !== 'N/A' ? userData.email_id : '',
            contactNumber: userData.contact_number && userData.contact_number !== 'N/A' ? userData.contact_number : '',
            userId: userData.id,
            token: userToStore.token
          });
          setShowProfileVerification(true);
          setLoading(false);
        } else {
          dispatch(setGoogleUser(userToStore));
          setLoading(true);
          setTimeout(() => {
            navigate('/sell-properties');
          }, 1500);
        }
      } catch (error) {
        console.error('Error handling direct user data:', error);
        showDialog('Error', 'Failed to process user data', 'error');
      }
    };

    const fetchAndSetUserProfile = async (userId, token) => {
      try {
        setLoading(true);
        dispatch(logout());
        await persistor.purge();

        console.log('Fetching user profile for ID:', userId);
        const response = await fetch(
          `https://propertydealstalk.com/api/users/getUserById?user_id=${userId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('User profile API response:', data);

        if (data.status === "SUCCESS" && data.result) {
          const userProfile = data.result;

          // ✅ Check if profile needs verification
          if (checkProfileCompleteness(userProfile)) {
            setProfileVerificationData({
              fullName: userProfile.full_name && userProfile.full_name !== 'N/A' ? userProfile.full_name : '',
              email: userProfile.email_id && userProfile.email_id !== 'N/A' ? userProfile.email_id : '',
              contactNumber: userProfile.contact_number && userProfile.contact_number !== 'N/A' ? userProfile.contact_number : '',
              userId: userProfile.id,
              token: token
            });
            setShowProfileVerification(true);
            setLoading(false);
          } else {
            const userToStore = {
              id: userProfile.id,
              fullName: userProfile.full_name,
              email: userProfile.email_id,
              contactNumber: userProfile.contact_number,
              token: token,
              authProvider: userProfile.auth_provider,
              googleId: userProfile.google_id,
              profileImage: userProfile.profile_image,
              location: userProfile.location,
              occupation: userProfile.occupation,
              professionalTitle: userProfile.professional_title,
              companyName: userProfile.company_name,
              yearsOfExperience: userProfile.years_of_experience,
              licenseNumber: userProfile.license_number,
              bio: userProfile.bio,
              website: userProfile.website,
              linkedin: userProfile.linkedin,
              facebook: userProfile.facebook,
            };

            localStorage.setItem('auth_token', token);
            localStorage.setItem('user_id', userProfile.id.toString());
            localStorage.setItem('user_email', userProfile.email_id);
            localStorage.setItem('user_name', userProfile.full_name);

            if (userProfile.auth_provider === 'google') {
              dispatch(setGoogleUser(userToStore));
            } else {
              dispatch(setUser(userToStore));
            }

            setTimeout(() => {
              navigate('/sell-properties');
              setLoading(false);
            }, 1500);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch user profile');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        showDialog('Error', `Failed to fetch user profile: ${error.message}`, 'error');
        setLoading(false);
      }
    };

    if (location.search || location.hash) {
      processGoogleResponse();
    }
  }, [location.search, location.hash, dispatch, navigate]);

  const handleCloseDialog = () => {
    setDialog({...dialog, open: false});
  };

  const showDialog = (title, message, severity = 'info') => {
    setDialog({ open: true, title, message, severity });
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && value.length <= 10)) {
      setPhoneNumber(value);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && value.length <= 6)) {
      setOtp(value);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_name');
      await persistor.purge();
      setLoading(true);
      window.location.href = 'https://propertydealstalk.com/api/users/auth/google/start';
    } catch (error) {
      console.error('Error initiating Google login:', error);
      setLoading(false);
    }
  };

  const handleGetOTP = async () => {
    if (phoneNumber.length !== 10) {
      showDialog('Error', 'Please enter a valid 10-digit phone number.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/requestMobileOtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_number: phoneNumber }),
      });

      const data = await response.json();

      if (response.ok && data.status === "SUCCESS") {
        setOtpPopup({ open: true, otp: data.result.otp });
        setShowOtpInput(true);
      } else {
        showDialog('Error', data.message || 'Failed to send OTP. Please try again.', 'error');
      }
    } catch (error) {
      console.error('OTP request error:', error);
      showDialog('Error', 'Something went wrong. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOtpPopup = () => {
    setOtp(otpPopup.otp);
    setOtpPopup({ open: false, otp: '' });
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 4) {
      showDialog('Error', 'Please enter a valid OTP.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/verifyMobileOtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_number: phoneNumber, otp: otp }),
      });

      const data = await response.json();

      if (response.ok && data.status === "SUCCESS") {
        const userData = data.result;

        // ✅ Check if profile needs verification for mobile login
        if (checkProfileCompleteness(userData)) {
          setProfileVerificationData({
            fullName: userData.full_name && userData.full_name !== 'N/A' ? userData.full_name : '',
            email: userData.email_id && userData.email_id !== 'N/A' ? userData.email_id : '',
            contactNumber: userData.contact_number && userData.contact_number !== 'N/A' ? userData.contact_number : '',
            userId: userData.id,
            token: userData.token
          });
          setShowProfileVerification(true);
          setLoading(false);
        } else {
          const userToStore = {
            id: userData.id,
            fullName: userData.full_name,
            email: userData.email_id,
            contactNumber: userData.contact_number,
            token: userData.token
          };

          localStorage.setItem('auth_token', userData.token);
          localStorage.setItem('user_id', userData.id);
          localStorage.setItem('user_email', userData.email_id);

          dispatch(setUser(userToStore));

          setTimeout(() => {
            navigate('/sell-properties');
            setLoading(false);
          }, 1500);
        }
      } else {
        setLoading(false);
        showDialog('Error', data.message || 'Invalid OTP. Please try again.', 'error');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setLoading(false);
      showDialog('Error', 'Something went wrong. Please try again later.', 'error');
    }
  };

  // NEW: Toggle between Google and Phone login
  const handleToggleLoginMethod = () => {
    setShowPhoneLogin(!showPhoneLogin);
    // Reset phone login state when switching
    if (showPhoneLogin) {
      setShowOtpInput(false);
      setOtp('');
      setPhoneNumber('');
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      sx={{ width: '100%', height: '100vh', overflow: 'hidden' }}
    >
      <Dialog
        open={dialog.open}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { borderRadius: '8px', width:'400px', height:'120px' } }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            textAlign: 'center',
            color: dialog.severity === 'success' ? '#4caf50' : dialog.severity === 'error' ? '#f44336' : '#0052ff',
            fontWeight: 500,
            pt: 3
          }}
        >
          {dialog.title}
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <DialogContentText id="alert-dialog-description" sx={{ textAlign: 'center' }}>
            {dialog.message}
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* OTP Popup Dialog */}
      <Dialog
        open={otpPopup.open}
        onClose={handleCloseOtpPopup}
        aria-labelledby="otp-dialog-title"
        PaperProps={{ style: { borderRadius: '8px', width: '400px', position: 'absolute', top: '20px' } }}
      >
        <DialogTitle
          id="otp-dialog-title"
          sx={{ textAlign: 'center', color: '#0052ff', fontWeight: 500, pt: 3 }}
        >
          Your OTP
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText sx={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#000' }}>
            {otpPopup.otp}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleCloseOtpPopup}
            variant="contained"
            sx={{
              bgcolor: '#0052ff',
              color: '#fff',
              px: 4,
              py: 1,
              fontWeight: 500,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': { bgcolor: '#0041cc' }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Profile Verification Dialog */}
      <Dialog
        open={showProfileVerification}
        onClose={() => !verificationLoading && setShowProfileVerification(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2,
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Outfit',
          fontWeight: 600,
          fontSize: '24px',
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Complete Your Profile
          <IconButton
            onClick={() => !verificationLoading && setShowProfileVerification(false)}
            disabled={verificationLoading}
            sx={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{
            fontFamily: 'Outfit',
            fontSize: '14px',
            color: '#666',
            mb: 3
          }}>
            Please complete your profile information to continue
          </Typography>

          {/* Full Name */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Outfit',
              fontSize: '14px',
              fontWeight: 500,
              mb: 1
            }}>
              Full Name <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your full name"
              value={profileVerificationData.fullName}
              onChange={(e) => handleVerificationChange('fullName', e.target.value)}
              error={!!verificationErrors.fullName}
              helperText={verificationErrors.fullName}
              disabled={verificationLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '8px',
                  fontFamily: 'Outfit',
                }
              }}
            />
          </Box>

          {/* Email */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Outfit',
              fontSize: '14px',
              fontWeight: 500,
              mb: 1
            }}>
              Email Address <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Enter your email"
              value={profileVerificationData.email}
              onChange={(e) => handleVerificationChange('email', e.target.value)}
              error={!!verificationErrors.email}
              helperText={verificationErrors.email}
              disabled={verificationLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '8px',
                  fontFamily: 'Outfit',
                }
              }}
            />
          </Box>

          {/* Mobile Number */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Outfit',
              fontSize: '14px',
              fontWeight: 500,
              mb: 1
            }}>
              Mobile Number <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter 10-digit mobile number"
              value={profileVerificationData.contactNumber}
              onChange={handleVerificationPhoneChange}
              error={!!verificationErrors.contactNumber}
              helperText={verificationErrors.contactNumber}
              disabled={verificationLoading}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: '8px',
                  fontFamily: 'Outfit',
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleVerificationSubmit}
            disabled={verificationLoading}
            variant="contained"
            fullWidth
            sx={{
              textTransform: 'none',
              fontFamily: 'Outfit',
              bgcolor: '#0052ff',
              borderRadius: '8px',
              py: 1.5,
              '&:hover': {
                bgcolor: '#0041cc',
              },
            }}
          >
            {verificationLoading ? 'Updating...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && <Loader />}

      {/* LEFT SIDE */}
    <Box
  width={isMobile ? '100%' : '40%'}
  height={isMobile ? '30vh' : '100%'}
  display="flex"
  flexDirection="column"
  justifyContent="center"
  alignItems="center"
  sx={{
    backgroundColor: '#AA16FF1A',
    padding: 4,
    position: 'relative',
  }}
>
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <img
      src={tited} // Your combined image (first image)
      alt="PropertyDealsTalk.com"
      style={{
        width: isMobile ? '280px' : '400px',
        height: 'auto',
        objectFit: 'contain'
      }}
    />
  </Box>
</Box>
      {/* RIGHT SIDE - LOGIN FORM */}
      <Box
        sx={{
          width: isMobile ? '100%' : '60%',
          height: isMobile ? 'calc(100vh - 30vh)' : '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fff',
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          overflowY: isMobile ? 'auto' : 'hidden',
          boxSizing: 'border-box',
                }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 320,
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            boxShadow: '0 0 20px rgba(0,0,0,0.05)',
            border: '1px solid #eee',
            mb: 2,
            mx: 'auto',
          }}
        >
          {/* MOVED INSIDE BOX: House Logo and Welcome Back */}
          <Box display="flex" justifyContent="center" sx={{ mb: 1 }}>
            <img src={houseLogo} alt="logo" style={{ width: 45, height: 45 }} />
          </Box>
          {/* <Typography variant="h6" textAlign="center" mb={2} fontWeight={500} fontSize="17px">
            Welcome Back
          </Typography> */}

          {/* CONDITIONAL RENDERING: Google Login (Default) or Phone Login */}
          {!showPhoneLogin ? (
            <>
              {/* GOOGLE LOGIN VIEW (PRIMARY) */}
              <Button
                fullWidth
                variant="contained"
                startIcon={<img src={googleIcon} alt="Google" style={{ width: 18 }} />}
                onClick={handleGoogleLogin}
                sx={{
                  bgcolor: '#0052ff',
                  color: '#fff',
                  py: 1.2,
                  fontWeight: 500,
                  borderRadius: 2,
                  textTransform: 'none',
                  mb: 2
                }}
              >
                Continue with Google
              </Button>
              
               <Button
                fullWidth
                variant="contained"
                startIcon={<Phone sx={{ width: 18, height: 18, color: '#fff' }} />}
                onClick={handleToggleLoginMethod}
                sx={{
                  bgcolor: '#0052ff',
                  color: '#fff',
                  py: 1.2,
                  fontWeight: 500,
                  borderRadius: 2,
                  textTransform: 'none',
                  mb: 2
                }}
              >
                Continue with Phone Number
              </Button>

              <Divider
                sx={{ my: 2, '&::before, &::after': { borderColor: '#ccc' }, color: '#536686', fontSize: '13px' }}
              >
                Or signup with phone Number
              </Divider>

              <Button
  fullWidth
  variant="outlined"
  startIcon={<Phone sx={{ width: 18, height: 18, color: '#536686' }} />}
  onClick={() => navigate('/register')}
  sx={{ 
    borderRadius: 2, 
    textTransform: 'none', 
    fontWeight: 500, 
    pt: 1.2, 
    pb: 1.2, 
    fontSize: '0.9rem',
    borderColor: '#536686',
    color: '#536686',
    '&:hover': {
      borderColor: '#0052ff',
      backgroundColor: 'rgba(0, 82, 255, 0.04)'
    }
  }}
>
  Sign up with Phone Number
</Button>
            </>
          ) : (
            <>
              {/* PHONE LOGIN VIEW */}
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Phone Number</Typography>
              <TextField
                fullWidth
                placeholder="Enter your phone number"
                margin="dense"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                disabled={showOtpInput}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ width: 18, height: 18, mr: 1, color: '#536686' }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ maxLength: 10, pattern: '[0-9]*' }}
              />

              {showOtpInput && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>OTP</Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter OTP"
                    margin="dense"
                    value={otp}
                    onChange={handleOtpChange}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <img src={lockIcon} alt="lock" style={{ width: 18, marginRight: 10 }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                  />
                </>
              )}

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5} mb={2} flexWrap="wrap">
                <FormControlLabel
                  control={<Checkbox size="small" />}
                  label="Remember me"
                  sx={{ '& .MuiFormControlLabel-label': { color: '#536686', fontSize: { xs: '12px', sm: '13px' } }, }}
                />
                {showOtpInput && (
                  <Typography
                    component="span"
                    onClick={handleGetOTP}
                    sx={{ fontSize: { xs: '12px', sm: '13px' }, color: '#0052ff', textDecoration: 'none', cursor: 'pointer' }}
                  >
                    Resend OTP
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                fullWidth
                disabled={loading || (showOtpInput ? otp.length < 4 : phoneNumber.length !== 10)}
                onClick={showOtpInput ? handleVerifyOtp : handleGetOTP}
                sx={{ bgcolor: '#0052ff', color: '#fff', py: 1.2, fontWeight: 500, borderRadius: 2, textTransform: 'none', mb: 2 }}
              >
                {showOtpInput ? 'Login with OTP' : 'Get OTP'}
              </Button>

              <Divider
                sx={{ my: 2, '&::before, &::after': { borderColor: '#ccc' }, color: '#536686', fontSize: '13px' }}
              >
                Or connect with
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<img src={googleIcon} alt="Google" style={{ width: 18 }} />}
                onClick={handleToggleLoginMethod}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, pt: 1.2, pb: 1.2, fontSize: '0.9rem' }}
              >
                Google
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;